import { useRef, useState } from 'react';
import { X, UploadCloud, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SpellersImportModal = ({ isOpen, onClose, onImported }) => {
  const fileRef = useRef(null);
  const [job, setJob] = useState(null); // {status, done_projects, total_projects, done_accounts, total_accounts, summary}
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef(null);

  if (!isOpen) return null;

  const poll = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`${API}/spellers/import/status/${jobId}`);
        const j = res.data.job;
        setJob(j);
        if (j.status === 'done') {
          clearInterval(pollRef.current);
          onImported && onImported();
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 2000);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setJob(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/spellers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJob({ status: 'running', done_projects: 0, total_projects: 0, done_accounts: 0, total_accounts: 0 });
      poll(res.data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start import');
    } finally {
      setUploading(false);
    }
  };

  const close = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    onClose();
  };

  const running = job && job.status === 'running';
  const done = job && job.status === 'done';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="spellers-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Import Spellers Data</h3>
          <button onClick={close} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400" data-testid="spellers-import-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!job && (
            <>
              <p className="text-sm text-gray-500">
                Upload the "Accounts + Projects" .xlsx tracker. This creates/updates real Accounts, Projects & Contacts in Zoho CRM — safe to re-run any time.
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl py-8 text-gray-500 hover:border-orange-400 hover:text-orange-500 transition-colors disabled:opacity-50"
                data-testid="spellers-import-dropzone"
              >
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <UploadCloud className="w-6 h-6" />}
                <span className="text-sm font-medium">{uploading ? 'Starting import…' : 'Click to choose .xlsx file'}</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="spellers-import-file-input"
              />
            </>
          )}

          {running && (
            <div className="space-y-3" data-testid="spellers-import-progress">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                Importing — this can take a couple of minutes for a big sheet…
              </div>
              {job.total_projects > 0 && (
                <div className="text-xs text-gray-400">
                  Projects {job.done_projects}/{job.total_projects} · Accounts {job.done_accounts}/{job.total_accounts}
                </div>
              )}
            </div>
          )}

          {done && (
            <div className="space-y-3" data-testid="spellers-import-summary">
              <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm">
                <CheckCircle2 className="w-5 h-5" /> Import complete
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-gray-400 text-xs mb-1">Projects</div>
                  <div className="text-gray-800">{job.summary.projects.created} created · {job.summary.projects.updated} updated</div>
                  {job.summary.projects.errors > 0 && <div className="text-red-500 text-xs mt-1">{job.summary.projects.errors} failed</div>}
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-gray-400 text-xs mb-1">Accounts</div>
                  <div className="text-gray-800">{job.summary.accounts.created} created · {job.summary.accounts.updated} updated</div>
                  {job.summary.accounts.errors > 0 && <div className="text-red-500 text-xs mt-1">{job.summary.accounts.errors} failed</div>}
                </div>
                <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                  <div className="text-gray-400 text-xs mb-1">Contacts</div>
                  <div className="text-gray-800">{job.summary.contacts.created} linked/created in CRM</div>
                </div>
              </div>
              {job.summary.error_details.length > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 text-amber-700 text-xs rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    {job.summary.error_details.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                </div>
              )}
              <button
                onClick={close}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#ee7203' }}
                data-testid="spellers-import-done-btn"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
